import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useClinic } from '@/contexts/ClinicContext';
import { webhookService, Webhook } from '@/services/webhookService';
import WebhookOverviewTab from '@/components/clinic/webhooks/WebhookOverviewTab';
import WebhookLogsTab from '@/components/clinic/webhooks/WebhookLogsTab';
import { Skeleton } from '@/components/ui/skeleton';

const WebhookDetailPage: React.FC = () => {
  const { clinic, isAdmin } = useClinic();
  const { webhookId } = useParams<{ webhookId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [webhook, setWebhook] = useState<Webhook | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Get base path based on current portal
  const getBasePath = () => {
    if (location.pathname.startsWith('/practitioner-portal')) {
      return '/practitioner-portal';
    }
    // Admin + clinic_staff both live at root.
    return '';
  };

  const basePath = getBasePath();

  useEffect(() => {
    if (!isAdmin) {
      navigate(`${basePath}/settings/webhooks`);
      return;
    }

    if (clinic?.id && webhookId) {
      loadWebhook();
    }
  }, [clinic?.id, webhookId, isAdmin, navigate, basePath]);

  const loadWebhook = async () => {
    if (!clinic?.id || !webhookId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await webhookService.getWebhook(clinic.id, webhookId);
      setWebhook(data);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load webhook';
      console.error('Failed to load webhook:', err);
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`${basePath}/settings/webhooks`);
  };

  if (!isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <div className="container mx-auto py-6 px-4 max-w-6xl">
        <div className="mb-6">
          <Skeleton className="h-10 w-64 mb-2" />
          <Skeleton className="h-6 w-96" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (error || !webhook) {
    return (
      <div className="container mx-auto py-6 px-4 max-w-6xl">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={handleBack}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Webhooks
          </Button>
        </div>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            {error || 'Webhook not found'}
          </h3>
          <p className="text-sm text-slate-600 mb-4">
            {error || 'The webhook you are looking for does not exist or you do not have permission to view it.'}
          </p>
          <Button onClick={handleBack}>Back to Webhooks</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 px-4 max-w-6xl">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={handleBack}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Webhooks
        </Button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">
              {webhook.name || 'Unnamed Webhook'}
            </h1>
            <p className="text-muted-foreground mt-1 break-all">
              {webhook.webhook_url}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              variant={webhook.enabled ? 'default' : 'secondary'}
              className={
                webhook.enabled
                  ? 'bg-green-100 text-green-800 border-green-200'
                  : 'bg-slate-100 text-slate-800 border-slate-200'
              }
            >
              {webhook.enabled ? 'Enabled' : 'Disabled'}
            </Badge>
            {webhook.failure_count >= 10 && (
              <Badge variant="destructive" className="bg-red-100 text-red-800 border-red-200">
                Unhealthy
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="logs">Logs</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-6">
          <WebhookOverviewTab webhook={webhook} />
        </TabsContent>
        <TabsContent value="logs" className="mt-6">
          {clinic?.id && webhookId && (
            <WebhookLogsTab clinicId={clinic.id} webhookId={webhookId} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WebhookDetailPage;

